import React, { useCallback, useMemo, useRef, useState } from "react";
import { useFragment } from "react-relay";

import { throttle } from "lodash";
import { graphql } from "relay-runtime";

import { useResizeObserver } from "~/lib/hooks";
import { decodeGlobalId } from "~/lib/relayUtil";
import { useAttendeePresenceContext } from "~/meetings/AttendeePresenceContext";
import AvatarList, { type AvatarListUser } from "~/ui/avatar/AvatarList";

import type { Attendees_note$key } from "~/__generated__/Attendees_note.graphql";
import type { Attendees_user$data as GraphQLAttendee } from "~/__generated__/Attendees_user.graphql";
import type { Attendees_viewer$key } from "~/__generated__/Attendees_viewer.graphql";

import styles from "./Attendees.module.css";

// A sort of compatibility type for users and avatars
type Attendee = Omit<GraphQLAttendee, "image"> & Pick<AvatarListUser, "image" | "isAnonymous">;

graphql`
	fragment Attendees_user on User @relay(mask: false) {
		id
		fullName
		image
		isExternal
		isGuest
		isTopUser
	}
`;

const viewerFragment = graphql`
	fragment Attendees_viewer on Viewer {
		user {
			...Attendees_user @relay(mask: false)
		}
	}
`;

const noteFragment = graphql`
	fragment Attendees_note on Note {
		noteuserSet {
			user {
				...Attendees_user @relay(mask: false)
			}
		}
	}
`;

export default function Attendees({
	viewer: viewerKey,
	note: noteKey,
}: {
	viewer: Attendees_viewer$key;
	note: Attendees_note$key;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const { attendeesPresenceInfo, anonymousAttendees } = useAttendeePresenceContext();

	const mixinPresenceInfo = useCallback(
		(attendee: Attendee): AvatarListUser & { isCurrentlyViewing: boolean } => {
			// NOTE: Why does the multicursor plugin use decoded IDs? Not knowing this caused me a few hours of headache -Wal
			const userId = attendee.isAnonymous ? attendee.id : decodeGlobalId(attendee.id)[1];
			const isCurrentlyViewing = attendeesPresenceInfo[userId]?.some(presenceInfo => presenceInfo.isCurrentlyViewing);

			return {
				...attendee,
				isCurrentlyViewing,
				outline: isCurrentlyViewing ? "solid" : "none",
				grayscale: !isCurrentlyViewing,
			};
		},
		[attendeesPresenceInfo],
	);

	const self = useMemo<AvatarListUser>(
		() => ({
			...mixinPresenceInfo(viewer.user),
			outline: "solid" as const,
			grayscale: false,
		}),
		[viewer.user, mixinPresenceInfo],
	);

	const allAttendees = useMemo<AvatarListUser[]>(
		() =>
			[self].concat(
				note.noteuserSet
					.filter(noteuser => noteuser.user.id !== self.id)
					.map(noteuser => mixinPresenceInfo(noteuser.user))
					.concat(
						anonymousAttendees.map(anon =>
							mixinPresenceInfo({
								...anon,
								isGuest: false,
								isExternal: false,
								isTopUser: false,
								isAnonymous: true,
							}),
						),
					)
					.sort((a, b) => {
						if (a.isCurrentlyViewing && !b.isCurrentlyViewing) return -1;
						if (!a.isCurrentlyViewing && b.isCurrentlyViewing) return 1;
						return a.fullName.localeCompare(b.fullName);
					}),
			),
		[self, note.noteuserSet, anonymousAttendees, mixinPresenceInfo],
	);

	const [numAvatars, setNumAvatars] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	useResizeObserver<HTMLDivElement>({
		ref: containerRef,
		onResize: useMemo(
			() =>
				throttle(({ width }) => {
					if (!width || !containerRef.current) return;

					const sizeMultiplier = 1.5; // This is the `em` value of "medium" for avatars.
					const avatarSizeInPixels = parseInt(getComputedStyle(containerRef.current).fontSize) * sizeMultiplier;

					// Show between 1 and 7 avatars, inclusive. Subtract 1 to account for "others" avatar
					const nextValue = Math.max(1, Math.min(8, Math.floor(width / avatarSizeInPixels)) - 1);

					if (nextValue !== numAvatars) {
						setNumAvatars(nextValue);
					}
				}, 200),
			[numAvatars],
		),
	});

	// TODO: "Viewed x minutes ago"
	// TODO: Coloured tooltips

	return (
		<AvatarList
			ref={containerRef}
			className={styles.attendees}
			users={allAttendees}
			selfId={self.id}
			size="medium"
			maxLength={numAvatars}
			detailsTooltipPlacement="bottom"
		/>
	);
}
